import { Box, Group, Loader, Modal } from '@mantine/core';
import {
  RemoteDoc,
  SharepointDocsProps,
  useSharepointDocs,
} from 'src/secret-menu/sharepoint/hooks/useSharepointDocs';
import { Table } from 'src/common/components/table/Table';
import { useState } from 'react';
import { Button } from 'src/components';
import { useDisclosure } from '@mantine/hooks';
import {
  CONTACTS_SELECTION_MODE,
  ContactsList,
} from 'src/clients/CreateFamily/AddContactsToFamily/ContactsList';
import { Client } from 'src/admin/users/types';
import { useTransferFileToClientMutation } from 'src/secret-menu/sharepoint/hooks/useTransferFileToClientMutation';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';

export default function SharepointDocs() {
  const [path, setPath] = useState<SharepointDocsProps>({ driveId: null, childId: null });
  const [selectedFile, setSelectedFile] = useState<{ driveId: string; childId: string } | null>(
    null,
  );
  const { docs, isLoading, isError } = useSharepointDocs(path);
  const [opened, { open, close }] = useDisclosure(false);
  const [client, setClient] = useState<Client | null>(null);
  const { transferFile, isLoading: transferLoading } =
    useTransferFileToClientMutation(selectedFile);

  if (isLoading) {
    return <Loader />;
  }

  if (isError) {
    return <div>Error loading sharepoint docs</div>;
  }

  if (!docs) {
    return (
      <Box>
        <div>No docs available</div>
      </Box>
    );
  }

  const columns = [
    {
      accessor: 'name',
      title: 'Name',
      sortable: true,
      sortKey: 'name',
      render: (doc: RemoteDoc) => (
        <div key={doc.name}>
          {doc.isFolder ? '📁' : '📄'} {doc.name}
        </div>
      ),
    },
    {
      accessor: 'name',
      title: 'Actions',
      sortable: false,
      width: 400,
      render: (doc: RemoteDoc) =>
        doc.downloadUrl ? (
          <Group>
            <Button
              size={'xs'}
              onClick={() => {
                if (!doc.downloadUrl) return;
                window.open(doc.downloadUrl, '_blank');
              }}
            >
              Download
            </Button>
            <Button
              size={'xs'}
              onClick={() => {
                setSelectedFile({ driveId: doc.driveId, childId: doc.id });
                open();
              }}
            >
              Transfer
            </Button>
          </Group>
        ) : (
          <></>
        ),
    },
  ];

  const hasParent = docs.length > 0 && docs[0].driveId && docs[0].parent && docs[0].parent.parentId;

  return (
    <Box>
      <Modal
        size={'xl'}
        opened={opened}
        onClose={close}
        title="Select the client you want to trasfer the file to"
        centered
      >
        <ContactsList
          selectedClients={client ? [client] : []}
          onSelectClient={client => {
            setClient(client);
          }}
          selectionMode={CONTACTS_SELECTION_MODE.SINGLE}
          showAvatar={false}
          showBorder={true}
        />
        <Box mt={'xl'}>
          <Button
            disabled={!client}
            loading={transferLoading}
            onClick={async () => {
              try {
                await transferFile(client!.id);
              } catch (e) {
                notifyAndLogError(
                  'Failed to transfer file',
                  'Something went wrong transferring the file. Please try again and contact support if the issue persists',
                  e,
                );
              } finally {
                close();
              }
            }}
          >
            Transfer file
          </Button>
        </Box>
      </Modal>
      <Table<RemoteDoc>
        title={hasParent ? docs[0].parent!.name || '' : 'Sharepoint files'}
        titleIcon={hasParent ? '←' : undefined}
        onTitleClick={
          hasParent
            ? () => {
                setPath({ driveId: docs[0].driveId, childId: docs[0].parent!.parentId });
              }
            : undefined
        }
        columns={columns}
        data={docs}
        isLoading={isLoading}
        onRowClick={doc => {
          if (!doc.isFolder) return;
          if (doc.driveId === null) {
            setPath({ driveId: null, childId: null });
          } else {
            setPath({ driveId: doc.driveId, childId: doc.id });
          }
        }}
      />
    </Box>
  );
}
